import { instance } from "api/apiBase";
import api from "api/landlord/address";
import { ScopedEntity } from "api/landlord/settings/types";
import { toast } from "react-toastify";
import { getSourceLabel } from "utils/getSourceLabel";
import { Additional } from "./templates";

export const loadUnitOptions = async (
  search: string,
  _,
  {
    page,
    propertyId,
    disabledTooltip = `You can invite applicants to a unit/site that accepts applications`
  }: {
    page: number;
    propertyId: number;
    disabledTooltip?: string;
  }
) => {
  if (!propertyId) return { options: [], hasMore: false };

  const data = await api.getUnitsOfProperty({
    propertyId,
    searchTerm: search || "",
    page
  });

  return {
    options: data?.items?.map((item) => ({
      value: item.id,
      label: item.title,
      sourceLabel: getSourceLabel(item),
      isDisabled: !item.magic_link?.active,
      tooltip: !item.magic_link?.active ? disabledTooltip : undefined
    })),
    hasMore: data.has_more,
    additional: {
      page: page + 1,
      propertyId,
      disabledTooltip
    }
  };
};

export const loadMoveUnitOptions = async (
  search: string,
  _: unknown,
  {
    page,
    propertyId,
    excludeUnitId
  }: {
    page: number;
    propertyId: number;
    excludeUnitId?: number;
  }
) => {
  if (!propertyId) return { options: [], hasMore: false };

  const data = await api.getUnitsOfProperty({
    propertyId,
    searchTerm: search || "",
    page
  });

  return {
    options: data?.items
      ?.filter((item) => item.id !== excludeUnitId)
      .map((item) => ({
        value: item.id,
        label: item.title,
        sourceLabel: getSourceLabel(item),
        unlisted: !item.magic_link?.active
      })),
    hasMore: data.has_more,
    additional: {
      page: page + 1,
      propertyId,
      excludeUnitId
    }
  };
};

export const loadBoomReportUnitOptions = async (
  search: string,
  _: unknown,
  additional: Additional
) => {
  const { page, propertyId } = additional;
  if (!propertyId) return { options: [], hasMore: false };

  const data = await api.getUnitsOfProperty({
    propertyId,
    searchTerm: search || "",
    page
  });

  return {
    options: data?.items?.map((item) => ({
      value: item.id,
      label: item.title,
      sourceLabel: getSourceLabel(item)
    })),
    hasMore: data.has_more,
    additional: {
      page: page + 1,
      propertyId
    }
  };
};

export const loadPropertyOptions = async (search: string, _, { page }) => {
  const data = await api.getProperties({
    searchTerm: search,
    page
  });

  return {
    options: data?.items?.map((item) => ({
      value: item.id,
      label: item.name || item?.address?.address1 || "",
      sourceLabel: getSourceLabel(item)
    })),
    hasMore: data.has_more,
    additional: {
      page: page + 1
    }
  };
};

const loadSelectOptions = async <T>(
  url: string,
  search: string,
  extractItems: (data: T) => ScopedEntity[] | undefined
) => {
  try {
    const { data } = await instance.get<T>(url, { params: { search } });

    return {
      options:
        extractItems(data)?.map((item) => ({
          value: item.id,
          label: item.name
        })) || [],
      hasMore: false,
      additional: {}
    };
  } catch {
    toast.error("Failed to load options. Please try again.");
    return { options: [], hasMore: false, additional: {} };
  }
};

export const loadPropertyOwnerOptions = async (search: string) =>
  loadSelectOptions<{ items: ScopedEntity[] }>(
    "/portal/v1/property_owners",
    search,
    (data) => data.items
  );

export const loadPropertyGroupOptions = async (search: string) =>
  loadSelectOptions<{ property_groups: ScopedEntity[] }>(
    "/portal/v1/property_groups",
    search,
    (data) => data.property_groups
  );
